import type { ProcessIR } from './processIr';
import type { ProcessNode, CanvasNode, NodeType as DomainNodeType, NodeSubtype } from './processNode';
import type { Duration, Frequency, Friction, Predict } from './processEdge';
import type { Segment } from '../migrations/legacyEdge';
import { NODE_CATALOG_BY_SUBTYPE } from './nodeCatalog';
export type {
  NodeType as ProcessNodeType,
  ActorSubtype,
  ResourceSubtype,
  ArtifactSubtype,
  EventSubtype,
  NodeSubtype,
  ProcessNode,
  CanvasNode,
  CanvasNodeType,
} from './processNode';
export { canvasNodeToProcessNode, processNodeToCanvasNode, resolveNodeSubtype } from './processNode';
export type {
  Friction,
  Blocking,
  Frequency,
  Duration,
  Predict,
  FlowType,
  StatementType,
  StepStatement,
  AssignStatement,
  WaitStatement,
  CallStatement,
  TriggerStatement,
  OnStatement,
  ExceptionStatement,
  SegmentStatement,
  ProcessEdge,
  ProcessEdgeRFC,
} from './processEdge';

// ── Node types ─────────────────────────────────────────────────────────────

export type NodeType = DomainNodeType;

export type PMNode = CanvasNode;

// ── Process ─────────────────────────────────────────────────────────────────


export type DslNodeKind = 'PERSON' | 'SOFTWARE' | 'TOOL' | 'CHANNEL';

export type DslLiteral = string | number | boolean | null;

export interface DslNodeDecl {
  kind: DslNodeKind;
  identifier: string;
  meta?: Record<string, DslLiteral>;
}

export interface StateDecl {
  name: string;
  value: DslLiteral;
  meta?: Record<string, DslLiteral>;
}

export interface EventDecl {
  name: string;
  meta?: Record<string, DslLiteral>;
}

export interface ProcessNodeRef {
  id: string;
}

export interface Process {
  id:        number;
  name:      string;
  color:     string;
  visible:   boolean;
  dslSchemaVersion: number;
  edgeIds:   string[];

  ir?:       ProcessIR;
  createdBy?: string;   // UID — para la versión colaborativa
}

// ── People ──────────────────────────────────────────────────────────────────

export interface Person {
  id:   string;   // coincide con el nodeId del nodo 'person'
  name: string;
}

// ── Cursor (colaboración en tiempo real) ────────────────────────────────────

export interface Cursor {
  cursorId?:    string;
  uid:         string;
  displayName: string;
  x:           number;   // coordenadas SVG
  y:           number;
  color:       string;
  updatedAt:   number;
}

// ── ViewBox ─────────────────────────────────────────────────────────────────

export interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

// ── Estado serializable completo (JSON save/load) ────────────────────────────

export interface SessionState {
  version:   number;
  nodes:     ProcessNode[];
  edges:     Segment[];
  layout: {
    nodes: PMNode[];
    viewBox: ViewBox;
  };
  processes: Process[];
  people:    Person[];
}

// ── Constantes de geometría ──────────────────────────────────────────────────

export const RING_RADII   = [72, 140, 210, 282, 355] as const;
export const RING_LABELS  = ['núcleo', 'interno', 'compartido', 'externo', 'frontera'] as const;
export const RING_FILLS   = ['#eef7f2', '#eef2fa', '#fdf6ee', '#f5f0fa', '#f7f5f0'] as const;
export const RING_STROKES = ['#a8d5bc', '#a8c0e8', '#f0d0a0', '#c8b0e0', '#c8c2b4'] as const;
export const RING_DESCS   = [
  'Personas del equipo · quién hace qué',
  'Herramientas internas · email · chat · calendario',
  'Conocimiento compartido · docs · repos · wikis',
  'Reuniones · sincronías · workshops · calls',
  'Herramientas externas · SaaS · APIs · proveedores',
] as const;

export const PALETTE = [
  '#e07b3a', '#4a9e6b', '#4a7fb5', '#b54a8c',
  '#c5a200', '#6a4ab5', '#b54a4a', '#3abdb5',
] as const;

export const BASE_VB: ViewBox = { x: -530, y: -430, w: 1060, h: 860 };

// ── Pesos para cálculo de impacto ────────────────────────────────────────────

export const FREQ_W: Record<Frequency, number> = {
  daily: 4, weekly: 3, monthly: 2, adhoc: 1,
};
export const DUR_W: Record<Duration, number> = {
  mins: 1, hours: 2, days: 3, weeks: 4,
};
export const FRIC_W: Record<Friction, number> = {
  none: 1, low: 1.5, medium: 2, high: 3,
};
export const PRED_W: Record<Predict, number> = {
  yes: 1, sometimes: 1.5, no: 2,
};

// ── Metadatos de tipos de nodo ───────────────────────────────────────────────

export const NODE_TYPE_META: Record<NodeSubtype, { icon: string; label: string }> = (
  Object.keys(NODE_CATALOG_BY_SUBTYPE) as NodeSubtype[]
).reduce((acc, subtype) => {
  const entry = NODE_CATALOG_BY_SUBTYPE[subtype];
  acc[subtype] = { icon: entry.icon, label: entry.label };
  return acc;
}, {} as Record<NodeSubtype, { icon: string; label: string }>);

export * from './nodeCatalog';
export * from './processIr';
